import React, { useState, useEffect, useCallback } from 'react';
import './pStyles/Donations.css';

const YOCO_PUBLIC_KEY = "pk_test_ed3c54a6gOol69qa7f45";

declare global {
  interface Window {
    Yoco: any;
  }
}

interface DonationImpact {
  amount: number;
  description: string;
  icon: string;
}

interface DonorInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface TransactionDetails {
  id: string;
  amount: number;
  currency: string;
  status: string;
  donorInfo: DonorInfo;
  donationType: 'one-time' | 'monthly';
  timestamp: string;
}

const impactData: DonationImpact[] = [
  { amount: 100, description: "Provides school supplies for 2 children", icon: "📚" },
  { amount: 250, description: "Feeds a family of 4 for one week", icon: "🍽️" },
  { amount: 500, description: "Plants 25 trees in community spaces", icon: "🌳" },
  { amount: 1000, description: "Supports health checkups for 10 people", icon: "🏥" },
  { amount: 2500, description: "Funds a community workshop program", icon: "🛠️" }
];

type PaymentStep = 'amount' | 'details' | 'payment' | 'success';

const DonationsPage: React.FC = () => {
  const [selectedAmount, setSelectedAmount] = useState<number>(250);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [donationType, setDonationType] = useState<'one-time' | 'monthly'>('one-time');
  const [currentStep, setCurrentStep] = useState<PaymentStep>('amount');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [yocoLoaded, setYocoLoaded] = useState<boolean>(false);
  const [transaction, setTransaction] = useState<TransactionDetails | null>(null);
  const [donorInfo, setDonorInfo] = useState<DonorInfo>({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  const predefinedAmounts = [100, 250, 500, 1000, 2500];

  useEffect(() => {
    const checkYoco = () => {
      if (window.Yoco || (window as any).YocoSDK) {
        if (!window.Yoco) window.Yoco = (window as any).YocoSDK;
        setYocoLoaded(true);
        return true;
      }
      return false;
    };

    if (checkYoco()) return;

    const interval = setInterval(() => {
      if (checkYoco()) clearInterval(interval);
    }, 500);
    
    return () => clearInterval(interval);
  }, []);

  const getFinalAmount = useCallback(() => {
    return customAmount ? parseFloat(customAmount) || 0 : selectedAmount;
  }, [customAmount, selectedAmount]);

  const getCurrentImpact = useCallback(() => {
    const amount = getFinalAmount();
    const impact = [...impactData].reverse().find(item => item.amount <= amount);
    return impact || impactData[0];
  }, [getFinalAmount]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const validateDonorInfo = useCallback(() => {
    return donorInfo.firstName.trim() && donorInfo.lastName.trim() && donorInfo.email.trim();
  }, [donorInfo]);

  const handleAmountSelection = () => {
    if (getFinalAmount() < 50) {
      alert('Minimum donation amount is R50');
      return;
    }
    setCurrentStep('details');
  };

  const handleDetailsSubmit = () => {
    if (!validateDonorInfo()) {
      alert('Please fill in all required fields');
      return;
    }
    setCurrentStep('payment');
  };

  const handlePayment = () => {
    if (!window.Yoco) {
      alert('Payment system not loaded. Please refresh.');
      return;
    }

    console.log('🚀 Processing payment...');
    setIsLoading(true);

    try {
      const amountInCents = Math.round(getFinalAmount() * 100);
      console.log('Amount in cents:', amountInCents);

      // Initialize Yoco SDK
      const yoco = new window.Yoco({ publicKey: YOCO_PUBLIC_KEY });
      console.log('✅ Yoco instance created:', yoco);

      // Show popup
      yoco.showPopup({
        amountInCents: amountInCents,
        currency: 'ZAR',
        name: 'SkillsBridge Donation',
        description: `${donationType === 'monthly' ? 'Monthly' : 'One-time'} donation`,
        callback: function (result: any) {
          console.log('💬 Payment callback received:', result);
          setIsLoading(false);
          
          if (result.error) {
            console.error('❌ Payment error:', result.error);
            alert(`Payment Error: ${result.error.message || 'Payment failed'}`);
          } else {
            console.log('✅ Payment successful, token:', result.id);
            handlePaymentSuccess(result.id);
          }
        }
      });

    } catch (error: any) {
      console.error('💥 Payment initialization error:', error);
      alert(`Error: ${error?.message || 'Failed to initialize payment'}`);
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = async (token: string) => {
    console.log('🔄 Processing payment token:', token);
    setIsLoading(true);
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';
      
      // Save donation to database
      const donationData = {
        first_name: donorInfo.firstName,
        last_name: donorInfo.lastName,
        email: donorInfo.email,
        phone: donorInfo.phone,
        amount: getFinalAmount(),
        donation_type: donationType,
        payment_token: token,
        transaction_id: token
      };
      
      console.log('💾 Saving donation to database:', donationData);
      
      const response = await fetch(`${API_BASE_URL}/api/donations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(donationData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Failed to save donation (Status: ${response.status})`);
      }
      
      const savedDonation = await response.json();
      console.log('✅ Donation saved to database:', savedDonation);
      
      const transactionDetails: TransactionDetails = {
        id: token,
        amount: getFinalAmount(),
        currency: 'ZAR',
        status: 'COMPLETED',
        donorInfo: donorInfo,
        donationType: donationType,
        timestamp: new Date().toISOString()
      };
      
      setTransaction(transactionDetails);
      setCurrentStep('success');
      setIsLoading(false);
    } catch (error: any) {
      console.error('❌ Payment processing failed:', error);
      alert(`Payment succeeded but failed to save donation record.\n\nError: ${error?.message || 'Unknown error'}`);
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedAmount(250);
    setCustomAmount('');
    setDonationType('one-time');
    setCurrentStep('amount');
    setIsLoading(false);
    setTransaction(null);
    setDonorInfo({
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    });
  };

  // Success page
  if (currentStep === 'success' && transaction) {
    return (
      <div className="donations-page success-page">
        <div className="donations-container">
          <div className="success-card">
            <div className="success-icon">🎉</div>
            <h1 className="success-title">Thank You for Your Donation!</h1>
            <p className="success-subtitle">
              Your generous contribution will make a real difference in our community.
            </p>
            
            <div className="transaction-details">
              <h2 className="details-title">Transaction Details</h2>
              <div className="details-grid">
                <div><strong>Transaction ID:</strong> {transaction.id}</div>
                <div><strong>Amount:</strong> {formatCurrency(transaction.amount)}</div>
                <div><strong>Type:</strong> {transaction.donationType === 'monthly' ? 'Monthly Donation' : 'One-time Donation'}</div>
                <div><strong>Status:</strong> <span className="status-completed">✓ Completed</span></div>
                <div><strong>Date:</strong> {new Date(transaction.timestamp).toLocaleDateString('en-ZA')}</div>
                <div><strong>Time:</strong> {new Date(transaction.timestamp).toLocaleTimeString('en-ZA')}</div>
              </div>
            </div>

            <div className="impact-display">
              <h3 className="impact-title">Your Impact: {getCurrentImpact().icon}</h3>
              <p className="impact-description">{getCurrentImpact().description}</p>
            </div>

            <div className="success-actions">
              <button className="btn-primary" onClick={resetForm}>Make Another Donation</button>
              <button className="btn-secondary" onClick={() => window.print()}>Print Receipt</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="donations-page">
      <section className="donations-hero">
        <div className="donations-container hero-content-wrapper">
          <div className="hero-content">
            <h1 className="donations-title">Make a Lasting Impact</h1>
            <p className="donations-subtitle">
              Your donation directly supports community projects that create positive change. 
              Every contribution, no matter the size, helps build stronger communities.
            </p>
          </div>
        </div>
      </section>

      <section className="donation-main">
        <div className="donations-container">
          <div className="donation-layout">
            
            <div className="donation-form-section">
              <div className="donation-card">
                <h2 className="form-title">Choose Your Impact</h2>
                
                {/* Step 1: Amount Selection */}
                {currentStep === 'amount' && (
                  <>
                    <div className="donation-type-toggle">
                      <button 
                        className={`toggle-btn ${donationType === 'one-time' ? 'active' : ''}`}
                        onClick={() => setDonationType('one-time')}
                      >
                        One-time
                      </button>
                      <button 
                        className={`toggle-btn ${donationType === 'monthly' ? 'active' : ''}`}
                        onClick={() => setDonationType('monthly')}
                      >
                        Monthly
                      </button>
                    </div>

                    <div className="amount-selection">
                      <h3 className="selection-title">Select Amount</h3>
                      <div className="amount-grid">
                        {predefinedAmounts.map(amount => (
                          <button
                            key={amount}
                            className={`amount-btn ${selectedAmount === amount && !customAmount ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedAmount(amount);
                              setCustomAmount('');
                            }}
                          >
                            R{amount}
                          </button>
                        ))}
                      </div>
                      
                      <div className="custom-amount">
                        <label htmlFor="custom-amount" className="form-label">Custom Amount</label>
                        <div className="custom-input-container">
                          <span className="currency-symbol">R</span>
                          <input
                            id="custom-amount"
                            type="number"
                            min="50"
                            placeholder="Enter amount"
                            value={customAmount}
                            onChange={(e) => {
                              setCustomAmount(e.target.value);
                              if (e.target.value) setSelectedAmount(0);
                            }}
                            className="custom-amount-input"
                          />
                        </div>
                      </div>
                    </div>

                    <button className="donate-btn" onClick={handleAmountSelection}>
                      <span>Continue to Details</span>
                      <span className="btn-arrow">→</span>
                    </button>
                  </>
                )}

                {/* Step 2: Donor Details */}
                {currentStep === 'details' && (
                  <>
                    <div className="step-indicator">
                      <div className="step active">1</div>
                      <div className="step-line"></div>
                      <div className="step active">2</div>
                      <div className="step-line"></div>
                      <div className="step">3</div>
                    </div>

                    <h3 className="step-title">Your Details</h3>
                    
                    <div className="donor-form">
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">First Name *</label>
                          <input
                            type="text"
                            className="form-input"
                            value={donorInfo.firstName}
                            onChange={(e) => setDonorInfo(prev => ({ ...prev, firstName: e.target.value }))}
                            placeholder="Enter your first name"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Last Name *</label>
                          <input
                            type="text"
                            className="form-input"
                            value={donorInfo.lastName}
                            onChange={(e) => setDonorInfo(prev => ({ ...prev, lastName: e.target.value }))}
                            placeholder="Enter your last name"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Email Address * (For Receipt)</label>
                        <input
                          type="email"
                          className="form-input"
                          value={donorInfo.email}
                          onChange={(e) => setDonorInfo(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="Enter your email address"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Phone Number</label>
                        <input
                          type="tel"
                          className="form-input"
                          value={donorInfo.phone}
                          onChange={(e) => setDonorInfo(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="+27 XX XXX XXXX"
                        />
                      </div>
                    </div>

                    <div className="donation-summary">
                      <p className="summary-amount">Donation Amount: {formatCurrency(getFinalAmount())}</p>
                      <p className="summary-type">
                        {donationType === 'monthly' ? 'Monthly recurring donation' : 'One-time donation'}
                      </p>
                    </div>

                    <div className="form-actions">
                      <button className="btn-secondary" onClick={() => setCurrentStep('amount')}>
                        ← Back to Amount
                      </button>
                      <button className="donate-btn" onClick={handleDetailsSubmit}>
                        <span>Continue to Payment</span>
                        <span className="btn-arrow">→</span>
                      </button>
                    </div>
                  </>
                )}

                {/* Step 3: Payment */}
                {currentStep === 'payment' && (
                  <>
                    <div className="step-indicator">
                      <div className="step completed">1</div>
                      <div className="step-line completed"></div>
                      <div className="step completed">2</div>
                      <div className="step-line"></div>
                      <div className="step active">3</div>
                    </div>

                    <h3 className="step-title">Payment Details</h3>
                    
                    <div className="donation-summary">
                      <p className="summary-amount">Donation Amount: {formatCurrency(getFinalAmount())}</p>
                      <p className="summary-type">
                        {donationType === 'monthly' ? 'Monthly recurring donation' : 'One-time donation'}
                      </p>
                    </div>

                    <div className="donor-summary">
                      <h4>Donor Information:</h4>
                      <p><strong>Name:</strong> {donorInfo.firstName} {donorInfo.lastName}</p>
                      <p><strong>Email:</strong> {donorInfo.email}</p>
                      {donorInfo.phone && <p><strong>Phone:</strong> {donorInfo.phone}</p>}
                    </div>

                    <div className="payment-section">
                      <div className="security-notice">
                        <span className="security-icon">🔒</span>
                        <span>Secure payment powered by Yoco</span>
                      </div>

                      <button 
                        className="payment-btn"
                        onClick={handlePayment}
                        disabled={isLoading || !yocoLoaded}
                      >
                        {isLoading ? (
                          <>
                            <span className="loading-spinner"></span>
                            Processing Payment...
                          </>
                        ) : !yocoLoaded ? (
                          'Loading Payment System...'
                        ) : (
                          `Pay ${formatCurrency(getFinalAmount())}`
                        )}
                      </button>
                    </div>

                    <div className="form-actions">
                      <button className="btn-secondary" onClick={() => setCurrentStep('details')}>
                        ← Back to Details
                      </button>
                    </div>
                  </>
                )}

                <div className="security-notice">
                  <span className="security-icon">🔒</span>
                  <span>Payment powered by Yoco & secured by SSL</span>
                </div>
              </div>
            </div>

            {/* Only show impact section on amount selection step */}
            {currentStep === 'amount' && (
              <div className="impact-section">
                <div className="impact-card">
                  <h2 className="impact-title">Your Impact</h2>
                  <div className="current-impact">
                    <div className="impact-amount">
                      <span className="impact-currency">R</span>
                      <span className="impact-number">{getFinalAmount()}</span>
                      {donationType === 'monthly' && <span className="impact-frequency">/month</span>}
                    </div>
                    <div className="impact-description">
                      <span className="impact-icon">{getCurrentImpact().icon}</span>
                      <p>{getCurrentImpact().description}</p>
                    </div>
                  </div>

                  <div className="impact-examples">
                    <h3 className="examples-title">What Your Donation Can Do</h3>
                    <div className="impact-list">
                      {impactData.map((impact, index) => (
                        <div key={index} className="impact-item">
                          <span className="impact-item-icon">{impact.icon}</span>
                          <div className="impact-item-content">
                            <span className="impact-item-amount">R{impact.amount}</span>
                            <span className="impact-item-description">{impact.description}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DonationsPage;